import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Table, WaitlistEntry, CreateWaitlistEntryData, UpdateWaitlistEntryData, WaitlistStatus, SavedPlayer } from '@shared/types';
import { db } from '../../firebase/firebase';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, writeBatch, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { EditablePosition } from './EditablePosition';
import { useSavedPlayers } from '../../hooks/useSavedPlayers';
import { SavedPlayersManager } from './SavedPlayersManager';

// Format timestamp for admin waitlist display (browser local timezone)
const formatJoinedAt = (timestamp: Timestamp | undefined): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${month}/${day}/${year} - ${hour12}:${minutes} ${ampm}`;
};

interface WaitlistManagerProps {
  roomId: string;
  table: Table;
  onClose: () => void;
}

export function WaitlistManager({ roomId, table, onClose }: WaitlistManagerProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showSavedPlayers, setShowSavedPlayers] = useState(false);
  const [editingEntry, setEditingEntry] = useState<WaitlistEntry | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [waitlistLoading, setWaitlistLoading] = useState(true);
  const [waitlistError, setWaitlistError] = useState<Error | null>(null);

  // Direct Firebase approach for waitlist
  useEffect(() => {
    if (!roomId || !table.id || !db) {
      setWaitlist([]);
      setWaitlistLoading(false);
      return;
    }

    const waitlistRef = collection(db, 'rooms', roomId, 'tables', table.id, 'waitlist');
    const waitlistQuery = query(waitlistRef, orderBy('position', 'asc'));
    
    const unsubscribe = onSnapshot(
      waitlistQuery,
      (snapshot) => {
        const waitlistData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as WaitlistEntry[];
        setWaitlist(waitlistData);
        setWaitlistLoading(false);
      },
      (err) => {
        setWaitlistError(err as Error);
        setWaitlistLoading(false);
      }
    );

    return () => unsubscribe();
  }, [roomId, table.id]);

  const handleAddEntry = async (data: CreateWaitlistEntryData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use direct Firebase calls instead of shared utility
      const waitlistRef = collection(db, 'rooms', roomId, 'tables', table.id, 'waitlist');
      
      // Calculate position - use MAX position + 1 to always add at the bottom
      // Using count would cause duplicates if positions have gaps from removed players
      const waitingEntries = waitlist.filter(entry => entry.status === 'waiting');
      const maxPosition = waitingEntries.length > 0 
        ? Math.max(...waitingEntries.map(e => e.position || 0)) 
        : 0;
      const nextPosition = maxPosition + 1;
      
      await addDoc(waitlistRef, {
        ...data,
        position: nextPosition,
        status: 'waiting',
        added_at: serverTimestamp(),
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
      
      setShowAddForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add player to waitlist');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEntry = async (entryId: string, data: UpdateWaitlistEntryData) => {
    try {
      setLoading(true);
      setError(null);
      
      // If status is "removed", delete the entry entirely
      if (data.status === 'removed') {
        const entryRef = doc(db, 'rooms', roomId, 'tables', table.id, 'waitlist', entryId);
        await deleteDoc(entryRef);
      } else {
        const entryRef = doc(db, 'rooms', roomId, 'tables', table.id, 'waitlist', entryId);
        await updateDoc(entryRef, {
          ...data,
          updated_at: serverTimestamp(),
        });
      }
      
      setEditingEntry(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update waitlist entry');
    } finally {
      setLoading(false);
    }
  };

  const handleSeatPlayer = async (entryId: string) => {
    if (!confirm('Are you sure you want to seat this player? This will move them to the seated players section.')) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Update player status to seated using direct Firebase calls
      const entryRef = doc(db, 'rooms', roomId, 'tables', table.id, 'waitlist', entryId);
      await updateDoc(entryRef, { 
        status: 'seated',
        updated_at: serverTimestamp()
      });
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to seat player');
    } finally {
      setLoading(false);
    }
  };

  const handleNotifyPlayer = async (entryId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Update player status to notified using direct Firebase calls
      const entryRef = doc(db, 'rooms', roomId, 'tables', table.id, 'waitlist', entryId);
      await updateDoc(entryRef, { 
        status: 'notified',
        updated_at: serverTimestamp()
      });
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to notify player');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveEntry = async (entryId: string) => {
    if (!confirm('Are you sure you want to remove this player from the waitlist?')) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Remove the entry using direct Firebase calls
      const entryRef = doc(db, 'rooms', roomId, 'tables', table.id, 'waitlist', entryId);
      await deleteDoc(entryRef);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove player from waitlist');
    } finally {
      setLoading(false);
    }
  };

  const handlePositionChange = async (entryId: string, newDisplayPosition: number) => {
    try {
      setLoading(true);
      setError(null);
      
      // Only handle waiting entries - notified and seated are not reorderable
      const waitingSection = [...waitingEntries];
      
      // Find current index in the waiting section
      const currentIndex = waitingSection.findIndex(e => e.id === entryId);
      if (currentIndex === -1) {
        throw new Error('Entry not found in waiting list');
      }
      
      // Clamp newDisplayPosition to valid range (1 to waitingSection.length)
      // If user types 999, it goes to the bottom
      const targetIndex = Math.max(1, Math.min(newDisplayPosition, waitingSection.length)) - 1;
      
      // If moving to same position, do nothing
      if (currentIndex === targetIndex) {
        setLoading(false);
        return;
      }
      
      // Reorder the waiting section array
      const [movedEntry] = waitingSection.splice(currentIndex, 1);
      if (!movedEntry) {
        throw new Error('Failed to move entry');
      }
      waitingSection.splice(targetIndex, 0, movedEntry);
      
      // Update database positions sequentially (1, 2, 3, ...)
      const batch = writeBatch(db);
      
      waitingSection.forEach((entry, index) => {
        const newDbPosition = index + 1; // 1-based positions
        if (entry.position !== newDbPosition) {
          const entryRef = doc(db, 'rooms', roomId, 'tables', table.id, 'waitlist', entry.id);
          batch.update(entryRef, {
            position: newDbPosition,
            updated_at: serverTimestamp()
          });
        }
      });
      
      await batch.commit();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update position');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = 'badge';
    switch (status) {
      case 'waiting':
        return `${baseClasses} badge-warning`;
      case 'notified':
        return `${baseClasses} badge-info`;
      case 'seated':
        return `${baseClasses} badge-success`;
      case 'removed':
        return `${baseClasses} badge-danger`;
      default:
        return `${baseClasses} badge-info`;
    }
  };

  const { waitingEntries, notifiedEntries, seatedEntries } = useMemo(() => {
    const entries = (waitlist as WaitlistEntry[]) || [];
    
    // Sort function to order entries by position (which preserves chronological order)
    const sortByPosition = (a: WaitlistEntry, b: WaitlistEntry) => {
      return (a.position || 0) - (b.position || 0);
    };
    
    return {
      waitingEntries: entries
        .filter((entry: WaitlistEntry) => entry.status === 'waiting')
        .sort(sortByPosition),
      notifiedEntries: entries
        .filter((entry: WaitlistEntry) => entry.status === 'notified')
        .sort(sortByPosition),
      seatedEntries: entries
        .filter((entry: WaitlistEntry) => entry.status === 'seated')
        .sort(sortByPosition),
    };
  }, [waitlist]);

  return (
    <ErrorBoundary>
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Waitlist - {table.name}
            </h2>
            <p className="text-sm text-gray-600">
              {table.game_type} • {table.stakes} • {table.number_of_tables} tables
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn btn-outline"
          >
            Close
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {showAddForm && (
          <AddWaitlistEntryForm
            roomId={roomId}
            onSubmit={handleAddEntry}
            onCancel={() => setShowAddForm(false)}
            loading={loading}
          />
        )}

        {editingEntry && (
          <EditWaitlistEntryForm
            entry={editingEntry}
            onSubmit={(data) => handleUpdateEntry(editingEntry.id, data)}
            onCancel={() => setEditingEntry(null)}
            loading={loading}
          />
        )}

        {showSavedPlayers && (
          <SavedPlayersManager
            roomId={roomId}
            onClose={() => setShowSavedPlayers(false)}
          />
        )}

        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Waiting List</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowSavedPlayers(true)}
              className="btn btn-outline"
              disabled={loading}
              title="Manage Saved Players"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Saved Players
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              Add Player
            </button>
          </div>
        </div>

        {waitlistLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" />
          </div>
        ) : waitlistError ? (
          <div className="text-center py-8">
            <p className="text-red-600">Error loading waitlist: {waitlistError.message}</p>
          </div>
        ) : waitingEntries.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No players on the waitlist</p>
          </div>
        ) : (
          <div className="space-y-3">
            {(waitingEntries as WaitlistEntry[]).map((entry: WaitlistEntry, index: number) => (
              <div key={entry.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <EditablePosition
                    position={index + 1}  // Display position (1-based index)
                    maxPosition={waitingEntries.length}
                    onUpdate={(newPosition) => handlePositionChange(entry.id, newPosition)}
                    loading={loading}
                  />
                  <div>
                    <p className="font-medium text-gray-900">{entry.name}</p>
                    {entry.phone && (
                      <p className="text-sm text-gray-500">{entry.phone}</p>
                    )}
                    {entry.added_at && (
                      <p className="text-xs text-gray-400">{formatJoinedAt(entry.added_at)}</p>
                    )}
                  </div>
                  {/* Add globe icon for web signups */}
                  {entry.added_by === 'self' && (
                    <div className="flex-shrink-0" title="Added via website">
                      <span className="text-lg">🌐</span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className={getStatusBadge(entry.status)}>
                    {entry.status}
                  </span>
                  {entry.status === 'waiting' && (
                    <button
                      onClick={() => handleNotifyPlayer(entry.id)}
                      className="btn btn-outline btn-sm"
                      disabled={loading}
                    >
                      Notify
                    </button>
                  )}
                  {entry.status === 'notified' && (
                    <button
                      onClick={() => handleSeatPlayer(entry.id)}
                      className="btn btn-success btn-sm"
                      disabled={loading}
                    >
                      Seat
                    </button>
                  )}
                  <button
                    onClick={() => setEditingEntry(entry)}
                    className="btn btn-outline btn-sm"
                    disabled={loading}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleRemoveEntry(entry.id)}
                    className="btn btn-danger btn-sm"
                    disabled={loading}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {notifiedEntries.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Notified Players</h3>
            <div className="space-y-3">
              {(notifiedEntries as WaitlistEntry[]).map((entry: WaitlistEntry, index: number) => (
                <div key={entry.id} className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <EditablePosition
                      position={index + 1}  // Display position based on filtered list index
                      maxPosition={notifiedEntries.length}
                      onUpdate={(newPosition) => handlePositionChange(entry.id, newPosition)}
                      loading={loading}
                    />
                    <div>
                      <p className="font-medium text-gray-900">{entry.name}</p>
                      {entry.phone && (
                        <p className="text-sm text-gray-500">{entry.phone}</p>
                      )}
                      {entry.added_at && (
                        <p className="text-xs text-gray-400">{formatJoinedAt(entry.added_at)}</p>
                      )}
                    </div>
                    {/* Add globe icon for web signups */}
                    {entry.added_by === 'self' && (
                      <div className="flex-shrink-0" title="Added via website">
                        <span className="text-lg">🌐</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className={getStatusBadge(entry.status)}>
                      {entry.status}
                    </span>
                    <button
                      onClick={() => handleSeatPlayer(entry.id)}
                      className="btn btn-success btn-sm"
                      disabled={loading}
                    >
                      Seat
                    </button>
                    <button
                      onClick={() => handleUpdateEntry(entry.id, { status: 'waiting' })}
                      className="btn btn-outline btn-sm"
                      disabled={loading}
                    >
                      Back to Waiting
                    </button>
                    <button
                      onClick={() => handleRemoveEntry(entry.id)}
                      className="btn btn-danger btn-sm"
                      disabled={loading}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {seatedEntries.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Seated Players</h3>
            <div className="space-y-3">
              {(seatedEntries as WaitlistEntry[]).map((entry: WaitlistEntry) => (
                <div key={entry.id} className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div>
                      <p className="font-medium text-gray-900">{entry.name}</p>
                      {entry.phone && (
                        <p className="text-sm text-gray-500">{entry.phone}</p>
                      )}
                      {entry.added_at && (
                        <p className="text-xs text-gray-400">{formatJoinedAt(entry.added_at)}</p>
                      )}
                    </div>
                    {/* Add globe icon for web signups */}
                    {entry.added_by === 'self' && (
                      <div className="flex-shrink-0" title="Added via website">
                        <span className="text-lg">🌐</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className={getStatusBadge(entry.status)}>
                      {entry.status}
                    </span>
                    <button
                      onClick={() => handleUpdateEntry(entry.id, { status: 'waiting' })}
                      className="btn btn-outline btn-sm"
                      disabled={loading}
                    >
                      Unseat
                    </button>
                    <button
                      onClick={() => handleRemoveEntry(entry.id)}
                      className="btn btn-danger btn-sm"
                      disabled={loading}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

// Add Waitlist Entry Form Component with Saved Players Autocomplete
interface AddWaitlistEntryFormProps {
  roomId: string;
  onSubmit: (data: CreateWaitlistEntryData) => void;
  onCancel: () => void;
  loading: boolean;
}

function AddWaitlistEntryForm({ roomId, onSubmit, onCancel, loading }: AddWaitlistEntryFormProps) {
  const [formData, setFormData] = useState<CreateWaitlistEntryData>({
    name: '',
    phone: '',
  });
  const [saveForFuture, setSaveForFuture] = useState(false);
  const [showNameDropdown, setShowNameDropdown] = useState(false);
  const [showPhoneDropdown, setShowPhoneDropdown] = useState(false);
  const [activeField, setActiveField] = useState<'name' | 'phone' | null>(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  
  const nameInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const nameDropdownRef = useRef<HTMLDivElement>(null);
  const phoneDropdownRef = useRef<HTMLDivElement>(null);
  
  const { 
    searchResults, 
    searchByName, 
    searchByPhone,
    clearSearch,
    savePlayer,
    isPlayerSaved
  } = useSavedPlayers(roomId);

  // Debounced search for name field
  const handleNameChange = useCallback((value: string) => {
    setFormData(prev => ({ ...prev, name: value }));
    setSelectedPlayerId(null); // Clear selection when typing
    
    if (value.length >= 2) {
      searchByName(value);
      setShowNameDropdown(true);
      setActiveField('name');
    } else {
      clearSearch();
      setShowNameDropdown(false);
    }
  }, [searchByName, clearSearch]);

  // Debounced search for phone field
  const handlePhoneChange = useCallback((value: string) => {
    setFormData(prev => ({ ...prev, phone: value }));
    setSelectedPlayerId(null); // Clear selection when typing
    
    // Check if we have at least 5 digits
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 5) {
      searchByPhone(value);
      setShowPhoneDropdown(true);
      setActiveField('phone');
    } else {
      clearSearch();
      setShowPhoneDropdown(false);
    }
  }, [searchByPhone, clearSearch]);

  // Handle selecting a saved player from dropdown
  const handleSelectPlayer = useCallback((player: SavedPlayer) => {
    setFormData({
      name: player.name,
      phone: player.phone || '',
    });
    setSelectedPlayerId(player.id);
    setShowNameDropdown(false);
    setShowPhoneDropdown(false);
    clearSearch();
    // Don't auto-check save since they're already saved
    setSaveForFuture(false);
  }, [clearSearch]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      if (nameDropdownRef.current && !nameDropdownRef.current.contains(target) &&
          nameInputRef.current && !nameInputRef.current.contains(target)) {
        setShowNameDropdown(false);
      }
      
      if (phoneDropdownRef.current && !phoneDropdownRef.current.contains(target) &&
          phoneInputRef.current && !phoneInputRef.current.contains(target)) {
        setShowPhoneDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If save for future is checked and player isn't already saved, save them
    if (saveForFuture && !selectedPlayerId && !isPlayerSaved(formData.name, formData.phone)) {
      try {
        const playerData: { name: string; phone?: string } = { name: formData.name };
        if (formData.phone) {
          playerData.phone = formData.phone;
        }
        await savePlayer(playerData);
      } catch (err) {
        console.error('Failed to save player for future:', err);
        // Continue with adding to waitlist even if save fails
      }
    }
    
    onSubmit(formData);
  };

  // Check if current form data matches an already saved player
  const isAlreadySaved = useMemo(() => {
    if (selectedPlayerId) return true;
    return isPlayerSaved(formData.name, formData.phone);
  }, [selectedPlayerId, formData.name, formData.phone, isPlayerSaved]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Player to Waitlist</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Player Name with Autocomplete */}
          <div className="relative">
            <label className="label">Player Name</label>
            <input
              ref={nameInputRef}
              type="text"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              onFocus={() => {
                if (formData.name.length >= 2) {
                  searchByName(formData.name);
                  setShowNameDropdown(true);
                  setActiveField('name');
                }
              }}
              className="input"
              placeholder="Enter player name or search saved players"
              required
              autoComplete="off"
            />
            
            {/* Name Autocomplete Dropdown */}
            {showNameDropdown && activeField === 'name' && searchResults.length > 0 && (
              <div 
                ref={nameDropdownRef}
                className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
              >
                {searchResults.map((player) => (
                  <button
                    key={player.id}
                    type="button"
                    onClick={() => handleSelectPlayer(player)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 border-b border-gray-100 last:border-b-0"
                  >
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{player.name}</p>
                      {player.phone && (
                        <p className="text-xs text-gray-500 truncate">{player.phone}</p>
                      )}
                    </div>
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">Saved</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Phone Number with Autocomplete */}
          <div className="relative">
            <label className="label">Phone Number (Optional)</label>
            <input
              ref={phoneInputRef}
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => handlePhoneChange(e.target.value)}
              onFocus={() => {
                const digits = (formData.phone || '').replace(/\D/g, '');
                if (digits.length >= 5) {
                  searchByPhone(formData.phone || '');
                  setShowPhoneDropdown(true);
                  setActiveField('phone');
                }
              }}
              className="input"
              placeholder="Enter phone number or search"
              autoComplete="off"
            />
            
            {/* Phone Autocomplete Dropdown */}
            {showPhoneDropdown && activeField === 'phone' && searchResults.length > 0 && (
              <div 
                ref={phoneDropdownRef}
                className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
              >
                {searchResults.map((player) => (
                  <button
                    key={player.id}
                    type="button"
                    onClick={() => handleSelectPlayer(player)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 border-b border-gray-100 last:border-b-0"
                  >
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{player.name}</p>
                      {player.phone && (
                        <p className="text-xs text-gray-500 truncate">{player.phone}</p>
                      )}
                    </div>
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">Saved</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Save for Future Visits Checkbox */}
          <div className="flex items-center space-x-2 pt-2">
            <input
              type="checkbox"
              id="saveForFuture"
              checked={saveForFuture}
              onChange={(e) => setSaveForFuture(e.target.checked)}
              disabled={isAlreadySaved}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
            />
            <label 
              htmlFor="saveForFuture" 
              className={`text-sm ${isAlreadySaved ? 'text-gray-400' : 'text-gray-700'}`}
            >
              {isAlreadySaved ? 'Player already saved' : 'Save player for future visits'}
            </label>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              Add Player
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Waitlist Entry Form Component
interface EditWaitlistEntryFormProps {
  entry: WaitlistEntry;
  onSubmit: (data: UpdateWaitlistEntryData) => void;
  onCancel: () => void;
  loading: boolean;
}

function EditWaitlistEntryForm({ entry, onSubmit, onCancel, loading }: EditWaitlistEntryFormProps) {
  const [formData, setFormData] = useState<UpdateWaitlistEntryData>({
    name: entry.name,
    phone: entry.phone,
    status: entry.status,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Filter out undefined values and empty strings for phone
    const cleanData: UpdateWaitlistEntryData = {};
    if (formData.name !== undefined) cleanData.name = formData.name;
    if (formData.phone !== undefined && formData.phone !== '') cleanData.phone = formData.phone;
    if (formData.status !== undefined) cleanData.status = formData.status;
    
    onSubmit(cleanData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Player</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Player Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Phone Number</label>
            <input
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input"
            />
          </div>

          <div>
            <label className="label">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as WaitlistStatus })}
              className="input"
            >
              <option value="waiting">Waiting</option>
              <option value="seated">Seated</option>
              <option value="removed">Removed</option>
            </select>
          </div>

          {entry.added_by === 'self' && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Player Check-In</p>
                  <p className="text-xs text-gray-600">Remove web signup indicator (globe icon)</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    onSubmit({ added_by: 'admin' });
                  }}
                  className="btn btn-outline btn-sm"
                  disabled={loading}
                >
                  Check In
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              Update Player
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
